// Parts for special orders: the suppliers we order from, what the customer pays for a part
// (the PHORA website price), how search results are ranked, and reading a tracking number out
// of a pasted shipping email. Shared by the server and the screens.

import { mulDivRound } from './money.js';
import { guessCarrier, type ShipCarrier } from './orders.js';
import { cleanMemberCode } from './member.js';

export const SUPPLIER_IDS = ['phora', 'plp', 'mobilesentrix', 'kracked'] as const;
export type SupplierId = (typeof SUPPLIER_IDS)[number];

export interface SupplierInfo {
  id: SupplierId;
  name: string;
  site: string;
}

/** In the order they're searched and shown: PHORA first (its price is the customer's price), then PhoneLCDParts and Kracked Screens. */
export const SUPPLIERS: SupplierInfo[] = [
  { id: 'phora', name: 'PHORA', site: 'phorascreenparts.com' },
  { id: 'plp', name: 'PhoneLCDParts', site: 'phonelcdparts.com' },
  { id: 'kracked', name: 'Kracked Screens', site: 'krackedscreensparts.com' },
  { id: 'mobilesentrix', name: 'MobileSentrix', site: 'mobilesentrix.com' },
];
const byId = (id: SupplierId) => SUPPLIERS.find((s) => s.id === id)!;

export function isSupplierId(v: unknown): v is SupplierId {
  return typeof v === 'string' && (SUPPLIER_IDS as readonly string[]).includes(v);
}

/** "PhoneLCDParts", "phonelcdparts.com", "PLP" and "plp" all mean the same supplier. */
export function supplierByName(name: string | null | undefined): SupplierInfo | null {
  const n = (name ?? '').trim().toLowerCase().replace(/^https?:\/\/(www\.)?/, '').replace(/\/.*$/, '');
  if (!n) return null;
  const squash = n.replace(/[^a-z0-9]/g, '');
  return (
    SUPPLIERS.find((s) => s.id === n || s.name.toLowerCase() === n || s.site === n || s.name.toLowerCase().replace(/[^a-z0-9]/g, '') === squash) ??
    (squash === 'phonelcd' || squash === 'lcdparts' || squash === 'phonelcdpartscom' ? byId('plp')
      : ['kracked', 'krackedscreens', 'krackedscreenscom', 'krackedscreensparts', 'krackedscreenspartscom'].includes(squash) ? byId('kracked')
      : squash === 'phorascreenparts' || squash === 'phorascreenpartscom' ? byId('phora') : null)
  );
}

/** A part found on a supplier's website. The supplier's own price stays on the server (it's our cost). */
export interface PartResult {
  supplier: SupplierId;
  name: string;
  sku: string | null;
  url: string | null;
  image: string | null;
  in_stock: boolean | null;
  /** What the customer pays: the PHORA website price. */
  price_cents: number | null;
  /**
   * 'phora': the PHORA item itself; 'phora_match': the same item on PHORA; 'phora_rule': PHORA's
   * pricing rule on the supplier's price; 'repair_markup' (repairs only, not screens): the
   * supplier's price plus the repair markup in Settings.
   */
  price_source: 'phora' | 'phora_match' | 'phora_rule' | 'repair_markup' | null;
}

// ---------------------------------------------------------------- pricing (the PHORA website price)

/** PHORA's markup on a supplier's price, by price band (the PHORA store's pricing rule). */
export const PHORA_MARKUP_TIERS: { underCents: number; addCents: number }[] = [
  { underCents: 2_000, addCents: 600 },
  { underCents: 3_000, addCents: 700 },
  { underCents: 4_000, addCents: 800 },
  { underCents: 5_000, addCents: 900 },
  { underCents: 7_000, addCents: 1_000 },
  { underCents: 10_000, addCents: 1_100 },
  { underCents: Number.POSITIVE_INFINITY, addCents: 1_200 },
];

/** PhoneLCDParts items on PHORA sell at PLP's price × 1.29. */
export const PHORA_PLP_MULTIPLIER = 1.29;

/**
 * What PHORA would charge for a part from a supplier, when PHORA doesn't list the same item:
 * PhoneLCDParts × 1.29; MobileSentrix and Kracked Screens + $6 to $12 by price band.
 */
export function phoraRulePriceCents(supplier: SupplierId, supplierCents: number): number {
  if (!Number.isFinite(supplierCents) || supplierCents <= 0) return 0;
  const c = Math.round(supplierCents);
  if (supplier === 'phora') return c;
  if (supplier === 'plp') return mulDivRound(c, 129, 100);
  const tier = PHORA_MARKUP_TIERS.find((t) => c < t.underCents) ?? PHORA_MARKUP_TIERS[PHORA_MARKUP_TIERS.length - 1];
  return c + tier.addCents;
}

export const PRICE_SOURCE_LABELS: Record<NonNullable<PartResult['price_source']>, string> = {
  phora: 'PHORA price',
  phora_match: 'PHORA price',
  phora_rule: 'PHORA pricing',
  repair_markup: 'part + repair markup',
};

// ---------------------------------------------------------------- matching and ranking

/** Title in a form where the same part from PLP and PHORA compares equal. */
export function normTitle(s: string): string {
  return s
    .toLowerCase()
    .replace(/\((only ground shipping|not returnable|ic transfer eligible|no ic transfer)\)/g, ' ')
    .replace(/&amp;|&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(for|with|and|the|compatible)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function words(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/(\d)(st|nd|rd|th) gen\b/g, '$1gen')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
}

const STOP = new Set(['for', 'the', 'and', 'with', 'a', 'an', 'of', 'to', 'part', 'parts', 'replacement', 'compatible', 'new', 'oem', 'genuine', 'original']);
const BRANDS = new Set(['apple', 'samsung', 'google', 'motorola', 'moto', 'lg', 'oneplus', 'tcl', 'nokia', 'sony', 'huawei', 'xiaomi', 'revvl', 't', 'mobile']);
const LINES = new Set(['iphone', 'ipad', 'galaxy', 'pixel', 'watch', 'macbook', 'ipod', 'switch', 'tab', 'note']);
const QUALIFIERS = new Set(['pro', 'max', 'plus', 'mini', 'ultra', 'fe', 'lite', 'edge', 'fold', 'flip', 'air', 'xl', 'xr', 'xs', 'neo', 'e', '5g']);
const PART_GROUPS: string[][] = [
  ['screen', 'display', 'lcd', 'oled', 'digitizer', 'assembly', 'incell', 'amoled', 'touch'],
  ['battery', 'batteries'],
  ['charging', 'charge', 'charger', 'port', 'dock', 'connector'],
  ['back', 'rear', 'housing', 'cover'],
  ['camera', 'lens'],
  ['speaker', 'earpiece', 'buzzer', 'ringer', 'loudspeaker'],
  ['frame', 'midframe', 'chassis', 'housing'],
];
const SCREEN_WORDS = new Set(PART_GROUPS[0]);
/** Words that mark a tool or accessory rather than the part itself. */
const ACCESSORY_WORDS = ['tempered', 'protector', 'film', 'mold', 'tester', 'tool', 'tools', 'machine', 'stencil', 'cleaning', 'opening', 'packing', 'laminating', 'separator', 'programmer', 'polarizer', 'oca'];
/** For a screen search, these mean a bit for a screen (an adhesive, a board, a cable), not the screen. */
const SCREEN_EXTRAS = ['adhesive', 'board', 'ic', 'cable', 'bracket', 'sticker', 'mesh', 'gasket', 'repair', 'flex', 'frame', 'connector'];

/** True when the text looks like a part number rather than words (no spaces, has digits). */
export function looksLikeSku(q: string): boolean {
  const t = q.trim();
  if (t.length < 4 || t.length > 40 || /\s/.test(t) || !/\d/.test(t)) return false;
  if (!/^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(t)) return false;
  return /-/.test(t) || /^\d{6,}$/.test(t) || (/[a-z]/i.test(t) && t.length >= 6);
}

const PART_QUERY_HINT = /\b(iphone|ipad|ipod|airpods?|galaxy|samsung|pixel|google|moto|motorola|oneplus|lg|tcl|revvl|xiaomi|redmi|huawei|nokia|sony|xperia|watch|macbook|switch|nintendo|playstation|xbox|screen|lcd|oled|display|digitizer|assembly|battery|charging|port|back glass|housing|frame|camera|lens|speaker|earpiece|flex|charger|adapter|cable|case|tempered|protector)\b/i;

/**
 * True when what was typed in the top search bar is about a part, so it's safe to ask the
 * suppliers' sites: a phone or part word, or a supplier SKU. Names, phone numbers, IMEIs,
 * emails and receipt numbers stay inside STORE OS.
 */
export function looksLikePartQuery(q: string): boolean {
  const t = q.trim();
  if (t.length < 3 || /@/.test(t)) return false;
  if (/^10\d{10}$/.test(t)) return true; // MobileSentrix product codes are 12 digits
  if (/^[\d\s()+#.-]+$/.test(t)) return false; // phone numbers, IMEIs, receipts
  if (/^[a-z][-\s]?\d{1,7}$/i.test(t) || (!/\s/.test(t) && cleanMemberCode(t))) return false; // O-1001, T-1001, B-1001, member cards
  if (PART_QUERY_HINT.test(t)) return true;
  return looksLikeSku(t) && /[a-z]/i.test(t) && /-/.test(t);
}

/** A supplier's product name as the customer sees it: shipping and return notes left off. */
export function customerPartName(name: string): string {
  return name
    .replace(/\s*\((only ground shipping|not returnable|non[- ]returnable|ic transfer eligible|no ic transfer|ground shipping only)\)/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200);
}

/** Words that name the kind of part ("screen", "battery") rather than the phone. */
function isPartWord(w: string): boolean {
  return PART_GROUPS.some((g) => g.includes(w)) || ['glass', 'flex', 'button', 'power', 'volume', 'face', 'id', 'sim', 'tray', 'vibrator', 'motor', 'antenna', 'board', 'logic', 'mic', 'microphone', 'proximity', 'sensor', 'home', 'ear'].includes(w);
}

/** The phone alone ("iphone 13 pro") from a part search ("iphone 13 pro screen"). */
export function modelQuery(q: string): string {
  const ws = words(q).filter((w) => !STOP.has(w));
  const kept = ws.filter((w) => !isPartWord(w));
  return kept.length && kept.length < ws.length ? kept.join(' ') : '';
}

/** Extra searches for sites whose search is literal: "screen" is listed as "LCD/OLED assembly" there. */
export function partQueryVariants(q: string): string[] {
  const base = q.trim().replace(/\s+/g, ' ');
  const out = [base];
  if (/\b(screen|display)\b/i.test(base)) {
    out.push(base.replace(/\b(screen|display)\b/i, 'lcd'), base.replace(/\b(screen|display)\b/i, 'oled'));
  } else if (/\bback glass\b/i.test(base)) {
    out.push(base.replace(/\bback glass\b/i, 'back cover glass'));
  } else if (/\bcharging port\b/i.test(base)) {
    out.push(base.replace(/\bcharging port\b/i, 'charging port flex'));
  }
  return [...new Set(out)];
}

function skuKey(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * How well a part fits what was typed. Model numbers have to match (an iPhone 13 search
 * shouldn't lead with iPhone 13 Pro parts), part words can match by meaning ("screen" finds
 * "OLED Assembly"), and accessories sink below the real part.
 */
export function scorePart(query: string, p: { name: string; sku: string | null; in_stock: boolean | null }): number {
  const q = query.trim();
  let score = 0;
  if (p.sku && q) {
    const a = skuKey(p.sku);
    const b = skuKey(q);
    if (a && a === b) score += 100;
    else if (b.length >= 4 && a.startsWith(b)) score += 20;
  }
  const qw = words(q).filter((w) => !STOP.has(w));
  const tw = words(p.name);
  const tset = new Set(tw);
  const qQual = new Set(qw.filter((w) => QUALIFIERS.has(w)));
  const isScreen = qw.some((w) => SCREEN_WORDS.has(w));
  for (const w of qw) {
    if (QUALIFIERS.has(w)) {
      if (!tset.has(w)) score -= 5;
      continue;
    }
    if (BRANDS.has(w)) {
      if (tset.has(w)) score += 1;
      continue;
    }
    if (LINES.has(w)) {
      score += tset.has(w) ? 2 : -2;
      continue;
    }
    if (/\d/.test(w)) {
      // A model number: present, and followed by exactly the qualifiers typed ("13" vs "13 pro").
      const at = tw.map((t, i) => (t === w ? i : -1)).filter((i) => i >= 0);
      if (!at.length) {
        score -= 7;
        continue;
      }
      const exact = at.some((i) => {
        const after = new Set<string>();
        for (let j = i + 1; j < tw.length && QUALIFIERS.has(tw[j]); j++) after.add(tw[j]);
        return after.size === qQual.size && [...after].every((x) => qQual.has(x));
      });
      score += exact ? 4 : -3;
      continue;
    }
    if (tset.has(w)) {
      score += 3;
      continue;
    }
    const group = PART_GROUPS.find((g) => g.includes(w));
    if (group && group.some((g) => tset.has(g))) score += 2;
    else score -= 2;
  }
  const accessory = ACCESSORY_WORDS.some((a) => tset.has(a) && !qw.includes(a));
  if (accessory) score -= 4;
  if (isScreen) {
    if (tw.some((w) => w === 'assembly' || w === 'oled' || w === 'lcd')) score += 2;
    if (accessory) score -= 3;
    if (SCREEN_EXTRAS.some((a) => tset.has(a) && !qw.includes(a))) score -= 3;
  }
  if (p.in_stock === true) score += 1;
  if (p.in_stock === false) score -= 1;
  return score;
}

/** Best fits first; ties keep the supplier's own order. */
export function rankParts<T extends { name: string; sku: string | null; in_stock: boolean | null }>(query: string, items: T[]): T[] {
  return items
    .map((p, i) => ({ p, i, s: scorePart(query, p) }))
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.p);
}

// ---------------------------------------------------------------- special order extras

export const ORDER_PAY_PLANS = ['now', 'pickup'] as const;
export type OrderPayPlan = (typeof ORDER_PAY_PLANS)[number];
export const ORDER_PAY_PLAN_LABELS: Record<OrderPayPlan, string> = { now: 'Paying now', pickup: 'Pays at pickup' };

export function isOrderPayPlan(v: unknown): v is OrderPayPlan {
  return typeof v === 'string' && (ORDER_PAY_PLANS as readonly string[]).includes(v);
}

/** Where a package is, in words anyone at the counter understands. */
export const TRACK_STATUSES = ['pre_transit', 'in_transit', 'out_for_delivery', 'delivered', 'pickup_ready', 'exception', 'unknown'] as const;
export type TrackStatus = (typeof TRACK_STATUSES)[number];
export const TRACK_STATUS_LABELS: Record<TrackStatus, string> = {
  pre_transit: 'Label made, not picked up yet',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  pickup_ready: 'Waiting at the carrier for pickup',
  exception: 'Delivery problem',
  unknown: 'Waiting for the first scan',
};

export function isTrackStatus(v: unknown): v is TrackStatus {
  return typeof v === 'string' && (TRACK_STATUSES as readonly string[]).includes(v);
}

/** Sticker for the back of the phone: the Brother label (the same one as a bought phone's), DYMO LabelWriter sizes, or the receipt printer. */
export const ORDER_LABEL_SIZES = {
  brother_62x100: 'Brother DK-1202 · 2.4 × 3.9 in',
  dymo_30334: 'DYMO 30334 · 2¼ × 1¼ in',
  dymo_30252: 'DYMO 30252 · 3½ × 1⅛ in',
  thermal: 'Receipt printer (80 mm)',
} as const;
export type OrderLabelSize = keyof typeof ORDER_LABEL_SIZES;

export function isOrderLabelSize(v: unknown): v is OrderLabelSize {
  return typeof v === 'string' && v in ORDER_LABEL_SIZES;
}

/** "2026-10-10" as "Sat, Oct 10" (a calendar date, so no time zone shift). */
export function formatShortDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  if (!m) return iso ?? '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(d);
}

// ---------------------------------------------------------------- tracking numbers in pasted text

export interface ExtractedShipping {
  tracking: string | null;
  carrier: ShipCarrier | null;
  /** The supplier's order number, when the pasted text has one ("Order #100123456"). */
  orderRef: string | null;
}

const SPACED = (n: string) => n.replace(/[\s-]+/g, '').toUpperCase();

/**
 * A tracking number from whatever was pasted: the number itself, or a whole shipping email
 * from PhoneLCDParts, MobileSentrix, Kracked Screens or PHORA. Spaces inside the number are fine.
 */
export function extractTracking(text: string | null | undefined): ExtractedShipping {
  const raw = (text ?? '').trim();
  const none: ExtractedShipping = { tracking: null, carrier: null, orderRef: null };
  if (!raw) return none;
  const single = SPACED(raw);
  if (raw.length <= 40 && /^[A-Z0-9]{8,34}$/.test(single) && (single.match(/\d/g)?.length ?? 0) >= 4) return { tracking: single, carrier: guessCarrier(single), orderRef: null };

  const lower = raw.toLowerCase();
  const orderMatch = /\border\s*(?:number|no\.?|#|id)?\s*[:#]?\s*#?\s*([A-Z]{0,4}-?\d{3,12})\b/i.exec(raw);
  const orderRef = orderMatch ? orderMatch[1] : null;
  const pick = (tracking: string, carrier: ShipCarrier | null): ExtractedShipping => ({ tracking, carrier: carrier ?? guessCarrier(tracking), orderRef });

  const ups = /\b1Z(?:[ ]?[0-9A-Z]){16}\b/i.exec(raw);
  if (ups) return pick(SPACED(ups[0]), 'ups');
  const uspsIntl = /\b[A-Z]{2}\d{9}US\b/.exec(raw);
  if (uspsIntl) return pick(uspsIntl[0], 'usps');
  const usps = /\b9[2345](?:[ ]?\d){18,20}\b/.exec(raw);
  if (usps) return pick(SPACED(usps[0]), 'usps');
  // "Tracking number: 1234 5678 9012" and similar (the first one that has a real number).
  for (const labeled of raw.matchAll(/track(?:ing)?\s*(?:number|no\.?|#|id|code)?\s*(?:is)?\s*[:#]?\s*([A-Z0-9](?:[ -]?[A-Z0-9]){7,33})/gi)) {
    // Keep only the groups that are part of the number ("7712 3456 7890"), not the words after it.
    const groups = labeled[1].split(/[ -]+/);
    const kept: string[] = [];
    for (const g of groups) {
      if (kept.length && !/\d/.test(g)) break;
      kept.push(g);
    }
    const t = SPACED(kept.join(''));
    if (!/\d{6,}/.test(t) || (orderRef && SPACED(orderRef) === t)) continue;
    const carrier = guessCarrier(t) ?? (lower.includes('fedex') ? 'fedex' : lower.includes('usps') ? 'usps' : /\bups\b/.test(lower) ? 'ups' : null);
    return pick(t, carrier ?? 'other');
  }
  if (lower.includes('fedex')) {
    const fx = /\b(\d{12}|\d{15}|\d{20}|\d{22})\b/.exec(raw);
    if (fx) return pick(fx[1], 'fedex');
  }
  if (lower.includes('dhl')) {
    const dhl = /\b(\d{10})\b/.exec(raw);
    if (dhl) return pick(dhl[1], 'other');
  }
  if (lower.includes('ontrac')) {
    const ot = /\b([CD]\d{14})\b/.exec(raw);
    if (ot) return pick(ot[1], 'other');
  }
  return { ...none, orderRef };
}
